import { buildApiUrl } from '../Api';
import { apiKey } from '../config';

export function resolveWebSocketPort(
  configuredPort,
  frontendPort,
  isDevelopment = process.env.NODE_ENV === 'development',
) {
  return configuredPort || (isDevelopment ? '5080' : frontendPort);
}

class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Set();
    this.connectionListeners = new Set();
    this.token = null;
    this._onMessage = this._onMessage.bind(this);
    this._onOpen = this._onOpen.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onError = this._onError.bind(this);
    this.reconnectDelay = 1000; // ms, simple backoff
    this.reconnectTimer = null;
    this._stopped = false;
    this.connectionEpoch = 0;
    this.hasOpened = false;
    this.connecting = false;
    this.connectionRequestId = 0;
  }

  start(token) {
    if (!token) {
      this.stop();
      return;
    }
    if (!this._stopped && this.token === token
      && (this.connecting || (this.ws
        && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)))) {
      return;
    }

    this.stop();
    this.token = token;
    this._stopped = false;
    this._connect();
  }

  stop() {
    this._stopped = true;
    this.token = null;
    this.connectionEpoch = 0;
    this.hasOpened = false;
    this.connecting = false;
    this.connectionRequestId += 1;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try { ws.close(); } catch { }
    }
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  subscribeConnection(fn) {
    this.connectionListeners.add(fn);
    return () => this.connectionListeners.delete(fn);
  }

  _emit(evt) {
    for (const fn of this.listeners) {
      try { fn(evt); } catch (e) { console.error('WS listener error', e); }
    }
  }

  async _connect() {
    if (this._stopped || !this.token || this.connecting || (this.ws
      && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN))) return;
    const token = this.token;
    const requestId = ++this.connectionRequestId;
    this.connecting = true;
    let ticket;
    try {
      const response = await fetch(buildApiUrl('/Client/ws-ticket'), {
        method: 'POST',
        headers: {
          ApiKey: apiKey,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('WebSocket ticket request failed');
      ({ ticket } = await response.json());
      if (!ticket) throw new Error('WebSocket ticket is missing');
    } catch (error) {
      if (!this._stopped && requestId === this.connectionRequestId) {
        this.connecting = false;
        this._onClose();
      }
      return;
    }
    if (this._stopped || token !== this.token || requestId !== this.connectionRequestId) return;
    this.connecting = false;
    const isSecure = window.location.protocol === 'https:';
    const protocol = isSecure ? 'wss' : 'ws';
    const host = process.env.REACT_APP_WS_HOST || window.location.hostname;
    const envPort = process.env.REACT_APP_WS_PORT;
    const inferredPort = window.location.port;
    const port = resolveWebSocketPort(envPort, inferredPort);
    const base = `${host}${port ? `:${port}` : ''}`;
    const url = `${protocol}://${base}/wsclient?ticket=${encodeURIComponent(ticket)}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      if (ws === this.ws) this._onOpen();
    };
    ws.onmessage = (event) => {
      if (ws === this.ws) this._onMessage(event);
    };
    ws.onclose = () => {
      if (ws !== this.ws) return;
      this.ws = null;
      this.connecting = false;
      this._onClose();
    };
    ws.onerror = (error) => {
      if (ws === this.ws) this._onError(error);
    };
  }

  _onOpen() {
    const reconnected = this.hasOpened;
    this.hasOpened = true;
    this.connectionEpoch += 1;
    this.reconnectDelay = 1000; // reset
    try { this.ws.send('Hello Server!'); } catch { }
    for (const fn of this.connectionListeners) {
      try {
        fn({
          state: 'open',
          epoch: this.connectionEpoch,
          reconnected,
        });
      } catch (e) {
        console.error('WS connection listener error', e);
      }
    }
  }

  _onMessage(event) {
    try {
      const json = JSON.parse(event.data);
      this._emit(json);
    } catch (e) {
      console.error('WS parse error', e);
    }
  }

  _onClose() {
    if (this._stopped || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
      this._connect();
    }, this.reconnectDelay);
  }

  _onError(err) {
    console.error('WebSocket error: ', err);
  }
}

const instance = new WebSocketService();
export default instance;
